import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["customSelect", "loader", "status", "statusText"]
  static values = {
    pageLanguage: { type: String, default: "en" },
    pollInterval: { type: Number, default: 1500 },
    pollTimeout: { type: Number, default: 7000 },
    stableMs: { type: Number, default: 350 }
  }

  connect() {
    this.shouldOpenSelect2AfterLoad = false
    this.initializeBrowserLanguageSession()

    this.handleBeforeCache = this.beforeCache.bind(this)
    this.handleBeforeVisit = this.beforeVisit.bind(this)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    document.addEventListener("turbo:before-visit", this.handleBeforeVisit)

    this.bindCustomChange()
    this.setupGoogleTranslate()
    this.startPollingGoogleCombo()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    document.removeEventListener("turbo:before-visit", this.handleBeforeVisit)
    this.clearPollTimer()
  }

  beforeCache() {
    this.clearPollTimer()
    this.setSwitchingState(false)
  }

  beforeVisit(event) {
    const language = localStorage.getItem("selected_lang") || "en"
    if (language === "en") return

    event.preventDefault()
    window.location.assign(event.detail.url)
  }

  openLanguageDropdown(event) {
    if (event) event.preventDefault()

    this.shouldOpenSelect2AfterLoad = true

    if (this.hasCustomSelectTarget && this.customSelectTarget.options.length > 1) {
      this.hideBootstrapDropdown()
      this.openSelect2()
    }
  }

  openSelect2() {
    if (!window.jQuery || !this.hasCustomSelectTarget) return

    this.showSelect2Container()

    const $select = jQuery(this.customSelectTarget)

    if (!$select.data("select2")) {
      setTimeout(() => this.openSelect2(), 150)
      return
    }

    setTimeout(() => {
      $select.select2("open")
    }, 100)
  }

  showSelect2Container() {
    const wrapper = this.customSelectTarget.closest(".goog-te-gadget")
    if (!wrapper) return

    wrapper.classList.remove("d-none")
    this.loaderTarget.classList.add("d-none")
  }

  hideBootstrapDropdown() {
    const btn = document.getElementById("langDropdownBtn")
    if (!btn || !window.bootstrap) return

    const dropdown =
      bootstrap.Dropdown.getInstance(btn) ||
      bootstrap.Dropdown.getOrCreateInstance(btn)

    dropdown.hide()
  }

  bindCustomChange() {
    if (!this.hasCustomSelectTarget) return
    if (this.customSelectTarget.dataset.changeBound === "1") return

    this.customSelectTarget.dataset.changeBound = "1"

    this.customSelectTarget.addEventListener("change", async () => {
      if (window.__gtSuppressLangChange) return
      if (this.customSelectTarget.dataset.switching === "1") return

      const lang = this.customSelectTarget.value
      this.setSwitchingState(true)
      localStorage.setItem("selected_lang", lang)
      document.dispatchEvent(
        new CustomEvent("itoons:language-change", {
          detail: { language: lang }
        })
      )

      if (lang === "en") {
        await this.resetTranslation()
        return
      }

      try {
        await this.changeTranslation(lang)
      } finally {
        this.setSwitchingState(false)
      }
    })
  }

  async changeTranslation(language) {
    this.setTranslationCookie(language)

    const gtCombo = document.querySelector(".goog-te-combo")
    const option = gtCombo?.querySelector(`option[value="${language}"]`)

    if (!gtCombo || !option) {
      window.location.reload()
      return
    }

    gtCombo.value = language
    gtCombo.dispatchEvent(new Event("change", { bubbles: true }))
    await this.nextFrame()
    await this.waitForTranslationDone()
  }

  setSwitchingState(switching) {
    if (!this.hasCustomSelectTarget) return

    this.customSelectTarget.dataset.switching = switching ? "1" : "0"
    this.customSelectTarget.disabled = switching

    if (this.hasStatusTarget) {
      this.statusTarget.classList.toggle("d-none", !switching)
    }

    if (switching && this.hasStatusTextTarget) {
      const language = this.customSelectTarget.selectedOptions[0]?.textContent
      this.statusTextTarget.textContent = language
        ? `Changing language to ${language}…`
        : "Changing language…"
    }

    if (window.jQuery) {
      jQuery(this.customSelectTarget).trigger("change.select2")
    }
  }

  setupGoogleTranslate() {
    window.__gtSuppressLangChange = false

    window.googleTranslateElementInit = () => {
      setTimeout(() => {
        const isInitialized = document.querySelector(".goog-te-combo")
        const translateContainer = document.getElementById("google_translate_element")

        if (!isInitialized && translateContainer && window.google?.translate) {
          new google.translate.TranslateElement(
            {
              pageLanguage: this.pageLanguageValue,
              autoDisplay: false
            },
            "google_translate_element"
          )
        }
      }, 1000)
    }

    if (!window.google || !window.google.translate) {
      this.loadGoogleScript()
    } else {
      window.googleTranslateElementInit()
    }
  }

  loadGoogleScript() {
    const existingScript = document.querySelector('script[data-google-translate="true"]')

    if (existingScript && !window.google?.translate) {
      console.warn("[GT] old script exists but Google not ready, removing")
      existingScript.remove()
    } else if (existingScript) {
      return
    }

    const script = document.createElement("script")
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
    script.async = true
    script.dataset.googleTranslate = "true"

    script.onload = () => {
      console.log("[GT] script loaded")
    }

    script.onerror = () => {
      console.error("[GT] script failed, retrying...")
      script.remove()

      setTimeout(() => {
        this.loadGoogleScript()
      }, 3000)
    }

    document.head.appendChild(script)

    setTimeout(() => {
      if (!window.google?.translate) {
        console.warn("[GT] script stuck, retrying...")
        script.remove()
        this.loadGoogleScript()
      }
    }, 10000)
  }

  startPollingGoogleCombo() {
    if (!this.hasCustomSelectTarget) return

    this.clearPollTimer()

    this.pollTimer = setInterval(() => {
      const gtCombo = document.querySelector(".goog-te-combo")
      if (!gtCombo || gtCombo.options.length < 2) return

      this.syncLanguageDropdown(gtCombo)
      this.clearPollTimer()

      if (this.shouldOpenSelect2AfterLoad) {
        this.shouldOpenSelect2AfterLoad = false
        this.hideBootstrapDropdown()

        setTimeout(() => {
          this.openSelect2()
        }, 250)
      }
    }, this.pollIntervalValue)
  }

  syncLanguageDropdown(gtCombo) {
    if (!this.hasCustomSelectTarget) return

    this.populateCustomDropdown(gtCombo)
    gtCombo.style.display = "none"
    this.restoreSelectedLanguage()
    this.showSelect2Container()
  }

  populateCustomDropdown(gtCombo) {
    const currentValue = this.customSelectTarget.value
    this.customSelectTarget.innerHTML = ""

    const options = []
    // Add English manually
    const englishOpt = document.createElement("option")
    englishOpt.value = "en"
    englishOpt.textContent = "English"
    options.push(englishOpt)

    for (let i = 0; i < gtCombo.options.length; i++) {
      const option = gtCombo.options[i]
      if (!option.value) continue
      if (option.value === "en") continue

      const opt = document.createElement("option")
      opt.value = option.value
      opt.textContent = option.textContent
      options.push(opt)
    }

    // English included in normal sort order
    options.sort((a, b) => a.textContent.localeCompare(b.textContent))

    options.forEach((opt) => this.customSelectTarget.appendChild(opt))

    if (currentValue && this.customSelectTarget.querySelector(`option[value="${currentValue}"]`)) {
      this.customSelectTarget.value = currentValue
    } else {
      this.customSelectTarget.value = "en"
    }
  }
  
  refreshSelect2() {
    // if (!window.jQuery || !this.hasCustomSelectTarget) return

    const $select = jQuery(this.customSelectTarget)

    // if ($select.data("select2")) {
      $select.trigger("change.select2")
    // }
  }

  select2Open() {
    setTimeout(() => {
      const input = document.querySelector(".select2-container--open .select2-search__field")
      if (input) input.focus()
    }, 50)
  }

  restoreSelectedLanguage() {
    const last = localStorage.getItem("selected_lang") || "en"

    if (!this.customSelectTarget.querySelector(`option[value="${last}"]`)) return

    window.__gtSuppressLangChange = true
    this.customSelectTarget.value = last
    this.refreshSelect2()

    setTimeout(() => {
      window.__gtSuppressLangChange = false
    }, 0)
  }

  setTranslationCookie(language) {
    const value = `/en/${language}`
    const host = window.location.hostname

    document.cookie = `googtrans=${value}; path=/; SameSite=Lax`

    const rootDomain = host.split(".").slice(-2).join(".")
    if (rootDomain !== host) {
      document.cookie =
        `googtrans=${value}; path=/; domain=.${rootDomain}; SameSite=Lax`
    }
  }

  initializeBrowserLanguageSession() {
    const browserSessionActive = document.cookie
      .split("; ")
      .some((cookie) => cookie === "itoons_language_session=1")

    if (browserSessionActive) return

    this.clearTranslationCookies()
    localStorage.setItem("selected_lang", "en")
    document.cookie = "itoons_language_session=1; path=/; SameSite=Lax"
  }

  async resetTranslation() {
    this.clearTranslationCookies()
    window.location.reload()
  }

  clearTranslationCookies() {
    const host = window.location.hostname
    this.deleteCookie("googtrans")
    this.deleteCookie("googtrans", "." + host)

    const rootDomain = host.split(".").slice(-2).join(".")
    if (rootDomain !== host) {
      this.deleteCookie("googtrans", "." + rootDomain)
    }
  }

  waitForTranslationDone({ timeoutMs = this.pollTimeoutValue, stableMs = this.stableMsValue } = {}) {
    return new Promise((resolve) => {
      let lastChange = Date.now()
      const start = Date.now()

      const observer = new MutationObserver(() => {
        lastChange = Date.now()
      })

      observer.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true
      })

      const tick = setInterval(() => {
        const now = Date.now()

        if (now - lastChange > stableMs) {
          cleanup()
          resolve(true)
          return
        }

        if (now - start > timeoutMs) {
          cleanup()
          resolve(false)
        }
      }, 1000)

      const cleanup = () => {
        clearInterval(tick)
        observer.disconnect()
      }
    })
  }

  deleteCookie(name, domain = null) {
    document.cookie =
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;` +
      (domain ? ` domain=${domain};` : "")
  }

  nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })
  }

  clearPollTimer() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }
}
